import React, { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import Honeypot from "./Honeypot";
import { apiError } from "../lib/errors";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";

/**
 * Customer-facing "Request Quote" modal. Submits to the existing /api/inquiries
 * endpoint so quote requests land in the admin Inquiries inbox.
 */
export default function RequestQuoteModal({ product, open, onOpenChange }) {
    const [f, setF] = useState({ name: "", email: "", phone: "", quantity: 1, message: "", website: "" });
    const [busy, setBusy] = useState(false);
    const cls = "bg-[#F7F3EB] border-[#E2D4C1] rounded-none focus-visible:ring-gold";

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post("/inquiries", {
                type: "tint_service",
                name: f.name,
                email: f.email,
                phone: f.phone || null,
                service_type: "Product Quote Request",
                vehicle_info: product ? `${product.name} (SKU ${product.mbg_sku || "—"}) × ${f.quantity}` : null,
                message: f.message || `Quote request for ${product?.name || "product"}, qty ${f.quantity}.`,
                website: f.website,
            });
            toast.success("Quote request sent. Our team will reach out shortly.");
            onOpenChange(false);
            setF({ name: "", email: "", phone: "", quantity: 1, message: "", website: "" });
        } catch (e) {
            toast.error(apiError(e, "Could not send request. Please try again."));
        }
        setBusy(false);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="bg-charcoal-800 border-[#E2D4C1] max-w-md rounded-none" data-testid="request-quote-modal">
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl">Request a Quote</DialogTitle>
                </DialogHeader>
                {product && (
                    <div className="flex items-center gap-3 border border-[#E2D4C1] p-3 bg-[#F7F3EB]">
                        {product.image && <img src={product.image} alt="" className="w-14 h-12 object-cover" />}
                        <div>
                            <div className="text-sm font-medium">{product.name}</div>
                            <div className="text-[11px] text-[#6B645C]">{product.mbg_sku}</div>
                        </div>
                    </div>
                )}
                <form onSubmit={submit} className="space-y-3" data-testid="request-quote-form">
                    <Honeypot value={f.website} onChange={(v) => setF({ ...f, website: v })} />
                    <Input aria-label="Full name" required placeholder="Full name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={cls} data-testid="rq-name" />
                    <Input aria-label="Email address" required type="email" placeholder="Email address" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={cls} data-testid="rq-email" />
                    <div className="grid grid-cols-2 gap-3">
                        <Input aria-label="Phone (optional)" placeholder="Phone (optional)" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className={cls} />
                        <Input aria-label="Quantity" type="number" min="1" placeholder="Quantity" value={f.quantity} onChange={(e) => setF({ ...f, quantity: +e.target.value })} className={cls} data-testid="rq-qty" />
                    </div>
                    <Textarea aria-label="Anything else we should know?" rows={3} placeholder="Anything else we should know?" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} className={cls} />
                    <Button type="submit" disabled={busy} data-testid="rq-submit" className="w-full bg-gold hover:bg-gold-hover text-white rounded-none uppercase tracking-widest text-xs font-semibold">
                        {busy ? "Sending…" : "Send Quote Request"}
                    </Button>
                </form>
            </DialogContent>
        </Dialog>
    );
}
